import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	CHALLENGE_TEMPLATES,
	addDaysToDateInput,
	buildChallengeTitle,
	CHALLENGE_DURATION_OPTIONS,
	MAX_CHALLENGE_DURATION_DAYS,
	clampChallengeDurationDays,
	clampChallengeEndDate,
	daysBetweenInclusive,
	daysForDurationChoice,
	durationChoiceFromDays,
	durationDaysForTemplate,
	findTemplate,
	isCustomTemplate,
	maxChallengeEndDate,
	todayDateInput,
	tomorrowDateInput
} from '../../constants/fitnessChallengeTemplates';
import {
	fitnessProfileDateFieldClassName,
	fitnessProfileDateInputClassName
} from '../FitnessProfile/shared/fitnessProfileReadingStyles';
import {
	accentPanelClass,
	accentSectionLabelClass,
	fieldLabelClass,
	hintClass,
	inputClass,
	nestedPanelClass,
	scheduleBlockClass,
	scheduleDateFieldLabelClass,
	scheduleDividerClass,
	schedulePanelClass,
	schedulePillActiveClass,
	schedulePillInactiveClass,
	scheduleQuestionClass,
	scheduleSelectClass,
	sectionLabelClass,
	templatePillInactiveClass
} from './challengeWireFormStyles';

function startModeForDate(iso) {
	if (!iso) return 'today';
	if (iso === todayDateInput()) return 'today';
	if (iso === tomorrowDateInput()) return 'tomorrow';
	return 'other';
}

function formatDisplayDate(iso) {
	if (!iso) return '';
	const d = new Date(`${iso}T12:00:00`);
	return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function TemplateParamField({ field, value, onChange }) {
	if (field.type === 'select') {
		return (
			<label className="block text-sm">
				<span className={fieldLabelClass}>{field.label}</span>
				<select
					className={`${inputClass} mt-1`}
					value={value ?? field.options[0]?.value ?? ''}
					onChange={(e) => onChange(field.key, e.target.value)}
				>
					{field.options.map((opt) => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</select>
			</label>
		);
	}

	return (
		<label className="block text-sm">
			<span className={fieldLabelClass}>{field.label}</span>
			<input
				type="number"
				className={`${inputClass} mt-1`}
				min={field.min}
				max={field.max}
				step={field.step ?? 1}
				value={value ?? ''}
				onChange={(e) => onChange(field.key, Number(e.target.value))}
			/>
		</label>
	);
}

export default function ChallengeTemplateConfigurator({
	templateKey,
	customParams,
	onTemplateKeyChange,
	onCustomParamsChange,
	startsOn,
	endsOn,
	onStartsOnChange,
	onEndsOnChange,
	title,
	onTitleChange,
	titleLocked,
	onTitleLockedChange
}) {
	const template = findTemplate(templateKey);
	const isCustom = isCustomTemplate(templateKey);
	const [durationChoice, setDurationChoice] = useState(() =>
		durationChoiceFromDays(durationDaysForTemplate(findTemplate(templateKey), customParams))
	);
	const [startMode, setStartMode] = useState(() => startModeForDate(startsOn));
	const startDateRef = useRef(null);

	const generatedTitle = useMemo(
		() => (isCustom ? '' : buildChallengeTitle(templateKey, customParams)),
		[templateKey, customParams, isCustom]
	);

	const durationDays = useMemo(() => {
		if (durationChoice === 'custom' && startsOn && endsOn) {
			return daysBetweenInclusive(startsOn, endsOn);
		}
		const presetDays = daysForDurationChoice(durationChoice);
		if (presetDays) return presetDays;
		return durationDaysForTemplate(template, customParams);
	}, [durationChoice, startsOn, endsOn, template, customParams]);

	useEffect(() => {
		if (isCustom || titleLocked) return;
		onTitleChange(generatedTitle);
	}, [generatedTitle, titleLocked, onTitleChange, isCustom]);

	useEffect(() => {
		setStartMode(startModeForDate(startsOn));
	}, [startsOn]);

	useEffect(() => {
		if (!startsOn || !endsOn) return;
		setDurationChoice((current) => {
			const computed = durationChoiceFromDays(daysBetweenInclusive(startsOn, endsOn));
			return current === 'custom' ? current : computed;
		});
	}, [startsOn, endsOn, templateKey]);

	useEffect(() => {
		if (!startsOn || !endsOn) return;
		const clamped = clampChallengeEndDate(startsOn, endsOn);
		if (clamped !== endsOn) onEndsOnChange(clamped);
	}, [startsOn, endsOn, onEndsOnChange]);

	function syncDurationParam(days) {
		if (!template.durationFromParam) return;
		const paramKey = template.durationFromParam;
		onCustomParamsChange((prev) => ({ ...prev, [paramKey]: clampChallengeDurationDays(days) }));
	}

	function applyDurationChoice(choice, startIso = startsOn) {
		setDurationChoice(choice);
		if (choice === 'custom' || !startIso) return;

		const days = daysForDurationChoice(choice);
		if (!days) return;

		onEndsOnChange(addDaysToDateInput(startIso, days));
		syncDurationParam(days);
	}

	function selectTemplate(nextKey) {
		const next = findTemplate(nextKey);
		const nextParams = { ...next.defaultParams };
		const days = durationDaysForTemplate(next, nextParams);

		onTemplateKeyChange(nextKey);
		onCustomParamsChange(nextParams);
		setDurationChoice(durationChoiceFromDays(days));
		if (startsOn) onEndsOnChange(addDaysToDateInput(startsOn, days));

		if (isCustomTemplate(nextKey)) {
			onTitleLockedChange(true);
			onTitleChange('');
		} else {
			onTitleLockedChange(false);
		}
	}

	function updateParam(key, value) {
		const nextValue =
			template.durationFromParam === key ? clampChallengeDurationDays(value) : value;
		const next = { ...customParams, [key]: nextValue };
		onCustomParamsChange(next);
		if (template.durationFromParam === key && startsOn) {
			onEndsOnChange(addDaysToDateInput(startsOn, nextValue));
		}
	}

	function handleStartsChange(iso) {
		onStartsOnChange(iso);
		setStartMode(startModeForDate(iso));
		if (durationChoice !== 'custom') {
			const days = daysForDurationChoice(durationChoice) || durationDays;
			onEndsOnChange(addDaysToDateInput(iso, days));
		} else if (endsOn) {
			onEndsOnChange(clampChallengeEndDate(iso, endsOn));
		}
	}

	function handleDurationSelect(event) {
		applyDurationChoice(event.target.value);
	}

	function handleCustomEndChange(iso) {
		const clamped = startsOn ? clampChallengeEndDate(startsOn, iso) : iso;
		onEndsOnChange(clamped);
		if (template.durationFromParam && startsOn) {
			syncDurationParam(daysBetweenInclusive(startsOn, clamped));
		}
	}

	const visibleTemplateFields = template.durationFromParam
		? template.fields.filter((f) => f.key !== template.durationFromParam)
		: template.fields;

	function selectStartMode(mode) {
		setStartMode(mode);
		if (mode === 'today') {
			handleStartsChange(todayDateInput());
			return;
		}
		if (mode === 'tomorrow') {
			handleStartsChange(tomorrowDateInput());
			return;
		}
		requestAnimationFrame(() => {
			const el = startDateRef.current;
			if (!el) return;
			el.focus();
			if (typeof el.showPicker === 'function') {
				try {
					el.showPicker();
				} catch {
					el.click();
				}
			} else {
				el.click();
			}
		});
	}

	function schedulePillClass(active) {
		return `rounded-full px-3 py-1.5 text-sm border transition ${
			active ? schedulePillActiveClass : schedulePillInactiveClass
		}`;
	}

	return (
		<div className="min-w-0 space-y-4">
			<div>
				<p className="text-sm font-medium text-gray-900 dark:text-dark-text">Challenge type</p>
				<p className={`${hintClass} mt-0.5`}>Pick a type, tune the details, then set when it runs.</p>
				<div className="mt-2 flex flex-wrap gap-2">
					{CHALLENGE_TEMPLATES.map((t) => (
						<button
							key={t.key}
							type="button"
							onClick={() => selectTemplate(t.key)}
							className={`rounded-full px-3 py-1.5 text-sm border transition ${
								templateKey === t.key
									? 'border-blue-600 bg-blue-600 text-white dark:border-blue-500 dark:bg-blue-600'
									: templatePillInactiveClass
							}`}
						>
							{t.label}
						</button>
					))}
				</div>
			</div>

			{visibleTemplateFields.length > 0 && (
				<div className={accentPanelClass}>
					<p className={accentSectionLabelClass}>{template.label} options</p>
					{visibleTemplateFields.map((field) => (
						<TemplateParamField
							key={field.key}
							field={field}
							value={customParams[field.key]}
							onChange={updateParam}
						/>
					))}
				</div>
			)}

			{isCustom ? (
				<div className={accentPanelClass}>
					<label className="block text-sm">
						<span className="font-medium text-gray-900 dark:text-dark-text">Challenge title</span>
						<span className="text-gray-500 dark:text-gray-400"> — describe the challenge</span>
						<input
							className={`${inputClass} mt-2`}
							value={title}
							onChange={(e) => onTitleChange(e.target.value)}
							placeholder="e.g. 2 minute plank, no screens after 9pm"
							maxLength={255}
							required
						/>
					</label>
				</div>
			) : (
				<div className={nestedPanelClass}>
					<div className="flex items-start justify-between gap-2">
						<div>
							<p className={sectionLabelClass}>Challenge title</p>
							<p className="mt-1 text-base font-semibold text-gray-900 dark:text-dark-text">
								{titleLocked ? title : generatedTitle}
							</p>
						</div>
						<button
							type="button"
							onClick={() => onTitleLockedChange(!titleLocked)}
							className="shrink-0 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
						>
							{titleLocked ? 'Use auto title' : 'Edit title'}
						</button>
					</div>
					{titleLocked && (
						<input
							className={`${inputClass} mt-3`}
							value={title}
							onChange={(e) => onTitleChange(e.target.value)}
							required
						/>
					)}
				</div>
			)}

			<div className={schedulePanelClass}>
				<p className={sectionLabelClass}>Schedule</p>

				<div className={scheduleBlockClass}>
					<p className={scheduleQuestionClass}>When does the challenge start?</p>
					<div className="flex flex-wrap gap-2">
						<button type="button" onClick={() => selectStartMode('today')} className={schedulePillClass(startMode === 'today')}>
							Today
						</button>
						<button
							type="button"
							onClick={() => selectStartMode('tomorrow')}
							className={schedulePillClass(startMode === 'tomorrow')}
						>
							Tomorrow
						</button>
						<button type="button" onClick={() => selectStartMode('other')} className={schedulePillClass(startMode === 'other')}>
							Other
						</button>
					</div>
					{startMode === 'other' && (
						<div className="min-w-0 w-full">
							<label htmlFor="challenge-start-date" className={scheduleDateFieldLabelClass}>
								Start date
							</label>
							<div className={fitnessProfileDateFieldClassName}>
								<input
									ref={startDateRef}
									id="challenge-start-date"
									type="date"
									className={fitnessProfileDateInputClassName}
									value={startsOn}
									onChange={(e) => handleStartsChange(e.target.value)}
									required
								/>
							</div>
						</div>
					)}
					{startMode !== 'other' && startsOn && (
						<p className={`${hintClass} pt-0.5`}>Starts {formatDisplayDate(startsOn)}</p>
					)}
				</div>

				<div className={scheduleDividerClass}>
					<div className={scheduleBlockClass}>
						<p className={scheduleQuestionClass}>How long does it last?</p>
						<select className={scheduleSelectClass} value={durationChoice} onChange={handleDurationSelect}>
							{CHALLENGE_DURATION_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
						<p className={`${hintClass} pt-1`}>
							Up to {MAX_CHALLENGE_DURATION_DAYS} days (3 months).
						</p>
					</div>
					{durationChoice === 'custom' ? (
						<div className="min-w-0 w-full">
							<label htmlFor="challenge-end-date" className={scheduleDateFieldLabelClass}>
								End date
							</label>
							<div className={fitnessProfileDateFieldClassName}>
								<input
									id="challenge-end-date"
									type="date"
									className={fitnessProfileDateInputClassName}
									value={endsOn}
									min={startsOn}
									max={startsOn ? maxChallengeEndDate(startsOn) : undefined}
									onChange={(e) => handleCustomEndChange(e.target.value)}
									required
								/>
							</div>
							{startsOn && (
								<p className={`${hintClass} pt-1`}>
									Select through {formatDisplayDate(maxChallengeEndDate(startsOn))} at latest.
								</p>
							)}
						</div>
					) : null}
					{startsOn && endsOn ? (
						<p className={`${hintClass} leading-relaxed`}>
							{startsOn === endsOn
								? `Runs ${formatDisplayDate(endsOn)} (1 day)`
								: `Runs ${formatDisplayDate(startsOn)} – ${formatDisplayDate(endsOn)} (${durationDays} days)`}
						</p>
					) : (
						<p className={hintClass}>Choose a start date to see the run window.</p>
					)}
				</div>
			</div>
		</div>
	);
}
